import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';

export default function ProjectView() {
  const { accountIndex, app_id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!app_id) return;

    let isMounted = true;
    const controller = new AbortController();

    const fetchProject = async () => {
      try {
        const response = await api.get(`/users/me/projects/${app_id}`, {
          signal: controller.signal
        });
        if (isMounted) {
          setProject(response.data);
          setLoading(false);
        }
      } catch (err) {
        if (err?.name === 'CanceledError') {
          return; // Ignore canceled requests
        }
        if (isMounted) {
          console.error(err);
          setError('Failed to load project details.');
          setLoading(false);
        }
      }
    };
    fetchProject();
    
    return () => { 
      isMounted = false;
      controller.abort();
    };
  }, [app_id]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-12 text-red-600">
        <p>{error || 'Project not found.'}</p>
        <button onClick={() => navigate(`/u/${accountIndex || 0}/dashboard/console/workspace`)} className="mt-4 text-indigo-600 hover:underline">
          Back to Workspace
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
        <button 
          onClick={() => navigate(`/u/${accountIndex || 0}/dashboard/console/workspace`)}
          className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          &larr; Back
        </button>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{project.application_name}</h2>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          project.is_active && project.approved_at && !project.suspended_at ? 'bg-emerald-100 text-emerald-700' 
          : !project.is_active || project.suspended_at ? 'bg-red-100 text-red-700'
          : 'bg-yellow-100 text-yellow-700'
        }`}>
          {project.is_active && project.approved_at && !project.suspended_at ? 'Live' 
          : !project.is_active || project.suspended_at ? 'Suspended'
          : 'PENDING_APPROVAL'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Integration Details</h3>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Client ID</dt>
              <dd className="font-mono text-slate-900 dark:text-slate-300 mt-1">{project.client_id}</dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Created At</dt>
              <dd className="text-slate-900 dark:text-slate-300 mt-1">{new Date(project.created_at).toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Description</dt>
              <dd className="text-slate-900 dark:text-slate-300 mt-1">{project.application_description || 'No description provided.'}</dd>
            </div>
          </dl>
        </div>

        <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Configuration</h3>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-slate-500 dark:text-slate-400 mb-1">Redirect URIs</dt>
              <dd>
                <ul className="list-disc pl-4 space-y-1 text-slate-900 dark:text-slate-300">
                  {Array.isArray(project.redirect_uris) && project.redirect_uris.map((uri, i) => (
                    <li key={i} className="font-mono text-xs truncate break-all">{uri}</li>
                  ))}
                </ul>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
